import { supabase } from './supabaseClient';
import { aNumero } from '../utils/validacion';
import { getNegocioId } from './negocioActual';

// Configuración del negocio (nombre, teléfono/WhatsApp, país, moneda).
export async function obtenerNegocio() {
  const { data, error } = await supabase
    .from('negocios')
    .select('*')
    .eq('id', getNegocioId())
    .single();
  if (error) throw error;
  return data;
}

// % de comisión bancaria POS/Link del negocio (default 4). Consulta tolerante:
// si la columna aún no existe, devuelve 4 sin romper.
export async function obtenerComisionPct() {
  const { data } = await supabase
    .from('negocios')
    .select('comision_pos_pct')
    .eq('id', getNegocioId())
    .maybeSingle();
  const pct = Number(data?.comision_pos_pct);
  return Number.isFinite(pct) && pct >= 0 ? pct : 4;
}

// Normaliza un "nombre corto" (slug) para el enlace del catálogo:
// minúsculas, sin acentos, espacios → guiones, solo letras/números/guiones.
export function normalizarSlug(v) {
  return (v || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export async function actualizarNegocio({ nombre, telefono, pais, moneda, logo_url, color_principal, instagram, comision_pos_pct, catalogo_publico, slug, zona_horaria, modo_viajes, rubro, vende, meta_pixel_id, razon_social, rtn, direccion, correo, pie_comprobante, portada_url, catalogo_bienvenida, tienda_diseno, tienda_envio, tienda_pago, tienda_combos }) {
  const cambios = {};
  // "¿Qué vendes?" (sql/144).
  if (['productos', 'servicios', 'ambos'].includes(vende)) cambios.vende = vende;
  // Portada y frase de bienvenida del catálogo (tienda en línea).
  if (portada_url !== undefined) cambios.portada_url = portada_url || null;
  if (tienda_pago !== undefined) cambios.tienda_pago = tienda_pago || {};
  if (tienda_combos !== undefined) cambios.tienda_combos = tienda_combos || {};
  if (tienda_envio !== undefined) cambios.tienda_envio = tienda_envio || {};
  if (tienda_diseno !== undefined) cambios.tienda_diseno = tienda_diseno || {};
  if (catalogo_bienvenida !== undefined) cambios.catalogo_bienvenida = catalogo_bienvenida?.trim().slice(0, 140) || null;
  // Datos que salen en el Comprobante de venta.
  if (razon_social !== undefined) cambios.razon_social = razon_social?.trim() || null;
  if (rtn !== undefined) cambios.rtn = rtn?.trim() || null;
  if (direccion !== undefined) cambios.direccion = direccion?.trim() || null;
  if (correo !== undefined) cambios.correo = correo?.trim() || null;
  if (pie_comprobante !== undefined) cambios.pie_comprobante = pie_comprobante?.trim() || null;
  if (nombre !== undefined) cambios.nombre = nombre.trim();
  if (modo_viajes !== undefined) cambios.modo_viajes = !!modo_viajes;
  if (rubro !== undefined) cambios.rubro = rubro || null;
  if (catalogo_publico !== undefined) cambios.catalogo_publico = !!catalogo_publico;
  if (slug !== undefined) cambios.slug = normalizarSlug(slug) || null;
  if (telefono !== undefined) cambios.telefono = telefono?.trim() || null;
  if (pais !== undefined) cambios.pais = pais?.trim() || null;
  if (zona_horaria !== undefined) cambios.zona_horaria = zona_horaria || 'America/Tegucigalpa';
  if (moneda !== undefined) cambios.moneda = moneda?.trim() || null;
  if (logo_url !== undefined) cambios.logo_url = logo_url || null;
  if (color_principal !== undefined) cambios.color_principal = color_principal || null;
  if (comision_pos_pct !== undefined) {
    const pct = aNumero(comision_pos_pct);
    // Si viene vacío o inválido, se deja el 4% por defecto del banco.
    cambios.comision_pos_pct = pct !== null && pct >= 0 && pct <= 100 ? pct : 4;
  }
  // Píxel de Meta: solo números (la BD también lo exige con un CHECK).
  if (meta_pixel_id !== undefined) {
    const id = String(meta_pixel_id || '').replace(/[^0-9]/g, '');
    if (id && (id.length < 6 || id.length > 20)) throw Object.assign(new Error('El número del píxel de Meta no parece correcto. Cópialo de nuevo del Administrador de eventos.'), { code: 'DATO_INVALIDO' });
    cambios.meta_pixel_id = id || null;
  }
  // Usuario de Instagram: se guarda sin @ ni URL (solo el usuario).
  if (instagram !== undefined) {
    cambios.instagram = (instagram || '')
      .trim().replace(/^@/, '')
      .replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')
      .replace(/\/+$/, '')
      .trim() || null;
  }

  const { data, error } = await supabase
    .from('negocios')
    .update(cambios)
    .eq('id', getNegocioId())
    .select()
    .single();
  if (error) throw error;
  return data;
}
